import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Stats {
  totalSales: number;
  totalExpenses: number;
  totalProfit: number;
  totalCustomers: number;
  totalEmployees: number;
  totalStores: number;
  recentSales: any[];
  monthSales: number;
  monthExpenses: number;
  monthProfit: number;
  monthBottles: number;
}

interface AIInsights {
  analysis: string;
  recommendations: string[];
  actions: string[];
}

interface PromoStats {
  readyForReward: number;
  oneAway: number;
  twoAway: number;
  threeAway: number;
  fourAway: number;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    totalSales: 0,
    totalExpenses: 0,
    totalProfit: 0,
    totalCustomers: 0,
    totalEmployees: 0,
    totalStores: 0,
    recentSales: [],
    monthSales: 0,
    monthExpenses: 0,
    monthProfit: 0,
    monthBottles: 0
  });
  const [visitStats, setVisitStats] = useState({
    totalVisits: 0,
    todayVisits: 0,
    weeklyVisits: 0
  });
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiInsights, setAiInsights] = useState<AIInsights | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [recentSales, setRecentSales] = useState<any[]>([]);
  const [recentExpenses, setRecentExpenses] = useState<any[]>([]);
  const [periodFilter, setPeriodFilter] = useState('month'); // today | week | month | year | all
  const [allSales, setAllSales] = useState<any[]>([]);
  const [allExpenses, setAllExpenses] = useState<any[]>([]);
  const [allVisits, setAllVisits] = useState<any[]>([]);
  const [promoStats, setPromoStats] = useState<PromoStats>({
    readyForReward: 0,
    oneAway: 0,
    twoAway: 0,
    threeAway: 0,
    fourAway: 0,
  });

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (!loading && allSales.length > 0) {
      recomputeStats();
    }
  }, [periodFilter]);

  const isInPeriod = (dateStr: string, period: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(todayStart.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    switch (period) {
      case 'today': return date >= todayStart;
      case 'week': return date >= weekStart;
      case 'month': return date >= monthStart;
      case 'year': return date >= yearStart;
      case 'all': return true;
      default: return date >= monthStart;
    }
  };

  const recomputeStats = () => {
    const filteredSales = allSales.filter(s => isInPeriod(s.created_at, periodFilter));
    const filteredExpenses = allExpenses.filter(e => isInPeriod(e.expense_date, periodFilter));
    const filteredVisits = allVisits.filter(v => isInPeriod(v.visited_at, periodFilter));

    const periodSales = filteredSales.reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0);
    const periodExpenses = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const periodProfit = periodSales - periodExpenses;

    const periodBottles = filteredSales.reduce((sum, s) => sum + (parseInt(s.quantity || 0)), 0);

    setStats(prev => ({
      ...prev,
      monthSales: periodSales,
      monthExpenses: periodExpenses,
      monthProfit: periodProfit,
      monthBottles: periodBottles,
    }));

    // Ventas y gastos recientes del período
    setRecentSales(filteredSales.slice(0, 5));
    setRecentExpenses(filteredExpenses.slice(0, 5));

    // Visitas
    const todayStr = new Date().toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const totalVisits = allVisits.length;
    const todayVisits = allVisits.filter(v => (v.visited_at || '').split('T')[0] === todayStr).length;
    const weeklyVisits = allVisits.filter(v => new Date(v.visited_at) >= weekAgo).length;
    setVisitStats({ totalVisits, todayVisits, weeklyVisits });
  };

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    } else {
      setUser(user);
      loadStats();
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const [salesRes, expensesRes, customersRes, employeesRes, visitsRes] = await Promise.all([
        supabase.from('sales').select('*').order('created_at', { ascending: false }),
        supabase.from('expenses').select('*').order('expense_date', { ascending: false }),
        supabase.from('customers').select('*'),
        supabase.from('employees').select('*'),
        supabase.from('site_visits').select('*')
      ]);

      const totalSales = salesRes.data?.reduce((sum, sale) => sum + parseFloat(sale.total_amount), 0) || 0;
      const totalExpenses = expensesRes.data?.reduce((sum, expense) => sum + parseFloat(expense.amount), 0) || 0;
      const totalProfit = totalSales - totalExpenses;

      // Guardar datos crudos para filtros
      const salesData = salesRes.data || [];
      const expensesData = expensesRes.data || [];
      const visitsData = visitsRes.data || [];
      setAllSales(salesData);
      setAllExpenses(expensesData);
      setAllVisits(visitsData);

      // Calcular stats de promoción 5+1 desde todas las ventas
      computePromoStats(salesData);

      // Totales globales (todo el tiempo)
      setStats(prev => ({
        ...prev,
        totalSales,
        totalExpenses,
        totalProfit,
        totalCustomers: customersRes.data?.length || 0,
        totalEmployees: employeesRes.data?.length || 0,
        totalStores: customersRes.data?.length || 0,
      }));

      // El resto de stats se calculan en recomputeStats via useEffect de periodFilter
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const generateAIInsights = () => {
    setLoadingInsights(true);
    
    // Simular análisis de IA
    setTimeout(() => {
      const profitMargin = stats.monthSales > 0 ? (stats.monthProfit / stats.monthSales) * 100 : 0;
      const expenseRatio = stats.monthSales > 0 ? (stats.monthExpenses / stats.monthSales) * 100 : 0;

      let analysis = '';
      const recommendations: string[] = [];
      const actions: string[] = [];

      // Análisis general
      if (profitMargin < 20) {
        analysis = `Tu negocio tiene un margen de ganancia del ${profitMargin.toFixed(1)}%, que está por debajo del promedio recomendado (20-30%). Esto indica que necesitas optimizar tus costos o ajustar tus precios para mejorar la rentabilidad.`;
      } else if (profitMargin > 40) {
        analysis = `¡Excelente! Tu negocio tiene un margen de ganancia del ${profitMargin.toFixed(1)}%, muy por encima del promedio. Esto demuestra una operación eficiente y precios competitivos.`;
      } else {
        analysis = `Tu negocio tiene un margen de ganancia saludable del ${profitMargin.toFixed(1)}%. Estás en el rango óptimo, pero siempre hay oportunidades de mejora.`;
      }

      // Recomendaciones basadas en datos
      if (profitMargin < 20) {
        recommendations.push('Revisa tus precios: Considera aumentar tus precios en un 10-15% para mejorar el margen sin perder competitividad.');
        recommendations.push('Reduce costos operativos: Negocia mejores precios con proveedores o busca alternativas más económicas.');
      }

      if (stats.totalCustomers < 10) {
        recommendations.push('Aumenta tu base de clientes: Implementa un programa de referidos donde clientes actuales reciban descuentos por traer nuevos compradores.');
        recommendations.push('Marketing digital: Invierte en publicidad en redes sociales enfocada en tu zona de distribución.');
      } else if (stats.totalCustomers > 50) {
        recommendations.push('Programa de fidelización: Crea un sistema de puntos o descuentos para clientes frecuentes.');
        recommendations.push('Segmentación de clientes: Ofrece precios especiales a tiendas que compren en mayor volumen.');
      }

      if (recentSales.length < 3) {
        recommendations.push('Impulsa las ventas: Crea promociones de temporada o descuentos por volumen para incentivar compras.');
        recommendations.push('Contacto proactivo: Llama a tus clientes regulares para recordarles hacer pedidos.');
      }

      if (expenseRatio > 70) {
        recommendations.push(`Tus gastos representan el ${expenseRatio.toFixed(1)}% de tus ventas. Identifica gastos innecesarios y elimínalos.`);
        recommendations.push('Optimiza rutas de entrega: Planifica mejor tus entregas para reducir costos de transporte.');
      }

      // Acciones concretas
      actions.push('Implementa pedidos por WhatsApp con un catálogo digital para facilitar las compras.');
      actions.push('Crea paquetes promocionales: "Compra 10 botellones y lleva 1 gratis".');
      actions.push('Ofrece suscripciones mensuales con entrega programada para generar ingresos recurrentes.');
      actions.push('Analiza tus horas pico de venta y asegura disponibilidad de producto en esos momentos.');
      actions.push('Establece alianzas con gimnasios, oficinas y restaurantes para ventas al por mayor.');
      actions.push('Implementa un sistema de recordatorios automáticos para clientes que no han comprado en 2 semanas.');

      setAiInsights({
        analysis,
        recommendations: recommendations.slice(0, 5),
        actions: actions.slice(0, 6)
      });

      setLoadingInsights(false);
      setShowInsightsModal(true);
    }, 1500);
  };

  const computePromoStats = (salesData: any[]) => {
    const customerCounts: Record<string, number> = {};
    salesData.forEach((s) => {
      if (
        s.customer_id &&
        s.counts_toward_promotion !== false &&
        parseFloat(s.total_amount || 0) > 0 &&
        s.status !== 'cancelled'
      ) {
        customerCounts[s.customer_id] = (customerCounts[s.customer_id] || 0) + (parseInt(s.quantity) || 0);
      }
    });

    const stats: PromoStats = {
      readyForReward: 0,
      oneAway: 0,
      twoAway: 0,
      threeAway: 0,
      fourAway: 0,
    };

    Object.values(customerCounts).forEach((count) => {
      const mod = count % 5;
      if (count > 0 && mod === 0) stats.readyForReward++;
      else if (mod === 4) stats.oneAway++;
      else if (mod === 3) stats.twoAway++;
      else if (mod === 2) stats.threeAway++;
      else if (mod === 1) stats.fourAway++;
    });

    setPromoStats(stats);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case 'today': return 'de Hoy';
      case 'week': return 'de la Semana';
      case 'month': return 'del Mes';
      case 'year': return 'del Año';
      case 'all': return 'Totales';
      default: return 'del Mes';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout currentPage="dashboard">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Panel de Control</h2>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">Resumen general del negocio</p>
      </div>

      {/* AI Insights Button */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={generateAIInsights}
          disabled={loadingInsights}
          className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all whitespace-nowrap flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          {loadingInsights ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Analizando...
            </>
          ) : (
            <>
              <i className="ri-lightbulb-flash-line text-xl"></i>
              Consejos de IA para Mejorar Ventas
            </>
          )}
        </button>
      </div>

      {/* Filtro de período */}
      <div className="mb-4 sm:mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <div className="flex flex-wrap gap-1">
          {[
            { key: 'today', label: 'Hoy' },
            { key: 'week', label: 'Esta Semana' },
            { key: 'month', label: 'Este Mes' },
            { key: 'year', label: 'Este Año' },
            { key: 'all', label: 'Todo' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPeriodFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                periodFilter === key
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* AI Insights Modal */}
      {showInsightsModal && aiInsights && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-pink-600">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center">
                  <i className="ri-lightbulb-flash-line text-purple-600 text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Análisis Inteligente</h3>
                  <p className="text-xs sm:text-sm text-purple-100">Recomendaciones personalizadas para tu negocio</p>
                </div>
              </div>
              <button 
                onClick={() => setShowInsightsModal(false)} 
                className="text-white hover:text-purple-100 transition-colors"
              >
                <i className="ri-close-line text-2xl sm:text-3xl"></i>
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Análisis General */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 sm:p-6">
                <div className="flex items-start gap-3">
                  <i className="ri-bar-chart-box-line text-blue-600 text-xl sm:text-2xl mt-1"></i>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Análisis General</h4>
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{aiInsights.analysis}</p>
                  </div>
                </div>
              </div>

              {/* Consejos */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                  <i className="ri-lightbulb-line text-yellow-600 text-lg sm:text-xl"></i>
                  Consejos para Mejorar
                </h4>
                <div className="space-y-3">
                  {aiInsights.recommendations.map((rec, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-xs sm:text-sm">{index + 1}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed flex-1">{rec}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Acciones Sugeridas */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                  <i className="ri-checkbox-circle-line text-green-600 text-lg sm:text-xl"></i>
                  Acciones Sugeridas
                </h4>
                <div className="space-y-2">
                  {aiInsights.actions.map((action, index) => (
                    <div key={index} className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                      <i className="ri-arrow-right-circle-fill text-green-600 text-lg sm:text-xl mt-0.5"></i>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed flex-1">{action}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowInsightsModal(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all whitespace-nowrap font-semibold text-sm sm:text-base"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Total Visitas Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-eye-line text-xl sm:text-2xl text-orange-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Total Visitas</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{visitStats.totalVisits.toLocaleString('es-CO')}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-4 pt-3 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500">Hoy</p>
              <p className="text-sm font-semibold text-orange-600">{visitStats.todayVisits}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Esta semana</p>
              <p className="text-sm font-semibold text-orange-600">{visitStats.weeklyVisits}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-xl sm:text-2xl text-green-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Ventas {getPeriodLabel()}</p>
              <p className="text-sm md:text-lg font-bold text-gray-900 whitespace-nowrap">{formatCurrency(stats.monthSales)}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.monthBottles.toLocaleString('es-CO')} botellones</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-line-chart-line text-xl sm:text-2xl text-blue-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm text-gray-600">Ganancia {getPeriodLabel()}</p>
              <p className="text-sm md:text-lg font-bold text-gray-900 whitespace-nowrap">{formatCurrency(stats.monthProfit)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Promo 5+1 Panel */}
      <div className="mb-4 sm:mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-gift-2-line text-teal-600 text-lg"></i>
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Promociones 5+1</h3>
          </div>
          <Link
            to="/admin/sales"
            className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 whitespace-nowrap"
          >
            Ver todas las ventas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Listos para premio */}
          <div className={`rounded-lg p-3 text-center transition-all ${
            promoStats.readyForReward > 0
              ? 'bg-green-50 border border-green-200'
              : 'bg-gray-50 border border-gray-100'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
              promoStats.readyForReward > 0 ? 'bg-green-500' : 'bg-gray-300'
            }`}>
              <i className={`text-white text-lg ${promoStats.readyForReward > 0 ? 'ri-gift-line' : 'ri-check-line'}`}></i>
            </div>
            <p className={`text-2xl font-bold ${promoStats.readyForReward > 0 ? 'text-green-700' : 'text-gray-400'}`}>
              {promoStats.readyForReward}
            </p>
            <p className={`text-xs font-medium mt-1 ${promoStats.readyForReward > 0 ? 'text-green-700' : 'text-gray-400'}`}>
              ¡Premio listo!
            </p>
          </div>

          {/* A 1 botellón */}
          <div className={`rounded-lg p-3 text-center transition-all ${
            promoStats.oneAway > 0
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-gray-50 border border-gray-100'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
              promoStats.oneAway > 0 ? 'bg-amber-500' : 'bg-gray-300'
            }`}>
              <i className="ri-drop-line text-white text-lg"></i>
            </div>
            <p className={`text-2xl font-bold ${promoStats.oneAway > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
              {promoStats.oneAway}
            </p>
            <p className={`text-xs font-medium mt-1 ${promoStats.oneAway > 0 ? 'text-amber-700' : 'text-gray-400'}`}>
              A 1 botellón
            </p>
          </div>

          {/* A 2 botellones */}
          <div className={`rounded-lg p-3 text-center transition-all ${
            promoStats.twoAway > 0
              ? 'bg-blue-50 border border-blue-200'
              : 'bg-gray-50 border border-gray-100'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
              promoStats.twoAway > 0 ? 'bg-blue-500' : 'bg-gray-300'
            }`}>
              <i className="ri-drop-line text-white text-lg"></i>
            </div>
            <p className={`text-2xl font-bold ${promoStats.twoAway > 0 ? 'text-blue-700' : 'text-gray-400'}`}>
              {promoStats.twoAway}
            </p>
            <p className={`text-xs font-medium mt-1 ${promoStats.twoAway > 0 ? 'text-blue-700' : 'text-gray-400'}`}>
              A 2 botellones
            </p>
          </div>

          {/* A 3 botellones */}
          <div className={`rounded-lg p-3 text-center transition-all ${
            promoStats.threeAway > 0
              ? 'bg-indigo-50 border border-indigo-200'
              : 'bg-gray-50 border border-gray-100'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
              promoStats.threeAway > 0 ? 'bg-indigo-500' : 'bg-gray-300'
            }`}>
              <i className="ri-drop-line text-white text-lg"></i>
            </div>
            <p className={`text-2xl font-bold ${promoStats.threeAway > 0 ? 'text-indigo-700' : 'text-gray-400'}`}>
              {promoStats.threeAway}
            </p>
            <p className={`text-xs font-medium mt-1 ${promoStats.threeAway > 0 ? 'text-indigo-700' : 'text-gray-400'}`}>
              A 3 botellones
            </p>
          </div>

          {/* A 4 botellones */}
          <div className={`rounded-lg p-3 text-center transition-all ${
            promoStats.fourAway > 0
              ? 'bg-gray-100 border border-gray-200'
              : 'bg-gray-50 border border-gray-100'
          }`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
              promoStats.fourAway > 0 ? 'bg-gray-500' : 'bg-gray-300'
            }`}>
              <i className="ri-drop-line text-white text-lg"></i>
            </div>
            <p className={`text-2xl font-bold ${promoStats.fourAway > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
              {promoStats.fourAway}
            </p>
            <p className={`text-xs font-medium mt-1 ${promoStats.fourAway > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
              A 4 botellones
            </p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Ventas {getPeriodLabel()}</h3>
          <div className="space-y-3 sm:space-y-4">
            {recentSales.slice(0, 5).map((sale) => (
              <div key={sale.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shopping-bag-line text-green-600 text-base sm:text-lg"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-xs sm:text-sm truncate">{sale.customer_name}</p>
                    <p className="text-xs text-gray-600">{sale.quantity} botellones</p>
                  </div>
                </div>
                <div className="text-right ml-2 flex-shrink-0">
                  <p className="font-semibold text-green-600 text-xs sm:text-sm">{formatCurrency(sale.total_amount)}</p>
                  <p className="text-xs text-gray-500">{new Date(sale.created_at).toLocaleDateString('es-CO', { month: 'short', day: 'numeric' })}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Gastos {getPeriodLabel()}</h3>
          <div className="space-y-3 sm:space-y-4">
            {recentExpenses.slice(0, 5).map((expense) => (
              <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-line text-red-600 text-base sm:text-lg"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-xs sm:text-sm truncate">{expense.description}</p>
                    <p className="text-xs text-gray-600 truncate">{expense.category}</p>
                  </div>
                </div>
                <div className="text-right ml-2 flex-shrink-0">
                  <p className="font-semibold text-red-600 text-xs sm:text-sm">{formatCurrency(expense.amount)}</p>
                  <p className="text-xs text-gray-500">{new Date(expense.expense_date).toLocaleDateString('es-CO', { month: 'short', day: 'numeric' })}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
